import {
	type Control,
	Controller,
	type FieldPath,
	type FieldValues,
} from "react-hook-form";
import {
	FlatRowSelect,
	type FlatRowSelectOption,
} from "web_myplann/Shared/Components/FlatRow/FlatRowSelect";

type Props<
	FormType extends FieldValues,
	TName extends FieldPath<FormType>,
	TValue extends string | number,
	TContext,
	TTransformed extends FieldValues | undefined,
> = {
	control: Control<FormType, TContext, TTransformed>;
	name: TName;
	label: string;
	options: FlatRowSelectOption<TValue>[];
	placeholder?: string;
	drawerTitle?: string;
	isLast?: boolean;
	multiple?: boolean;
	onValueChange?: (value: TValue) => void;
};

export const FlatRowFormSelect = <
	FormType extends FieldValues,
	TName extends FieldPath<FormType>,
	TValue extends string | number = string,
	TContext = unknown,
	TTransformed extends FieldValues | undefined = undefined,
>({
	control,
	name,
	label,
	options,
	placeholder,
	drawerTitle,
	isLast,
	multiple,
	onValueChange,
}: Props<FormType, TName, TValue, TContext, TTransformed>) => {
	return (
		<Controller
			control={control}
			name={name}
			render={({
				field: { value, onChange, onBlur },
				fieldState: { error },
			}) => {
				const sharedProps = {
					label,
					options,
					placeholder,
					onBlur,
					drawerTitle,
					isLast,
					error: !!error?.message,
					helperText: error?.message,
				};

				return multiple ? (
					<FlatRowSelect<TValue>
						{...sharedProps}
						multiple
						value={(value ?? []) as TValue[]}
						onChange={onChange}
					/>
				) : (
					<FlatRowSelect<TValue>
						{...sharedProps}
						value={(value ?? "") as TValue | ""}
						onChange={(next) => {
							onChange(next);
							onValueChange?.(next);
						}}
					/>
				);
			}}
		/>
	);
};
